import { Button, Chip, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SignalNotificationLog } from '@provision/signal-notification-log';
import React from 'react';
import { PartnerTimeZoneTooltip } from 'src/components/DateTime';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { useDisplayTime } from 'src/utils/hooks';
import { getRelativeDayLabel } from 'src/utils/relative-day-label';
import { SignalIncidentNotificationLog } from './signal-incident-notification-log';

type SignalIncidentUrgencyTone = 'neutral' | 'low' | 'normal' | 'urgent' | 'life-safety';

interface SignalIncidentRowIncident {
  signalIncidentId: string;
  partnerId?: number | null;
  createdAt: string;
  resolvedAt?: string | null;
  displayState: 'unresolved' | 'resolved';
  resolverDisplayName?: string | null;
  urgency?: {
    label: string;
    tone: SignalIncidentUrgencyTone;
  } | null;
  notificationLog?: SignalNotificationLog | null;
}

const outerRowSx: SxProps<Theme> = {
  mt: 1,
  width: '100%',
  pl: 1,
  pr: 2,
  boxSizing: 'border-box',
};

const iconSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  width: '0.8125rem',
  height: '0.8125rem',
  lineHeight: 1,
};

const overlineTextSx: SxProps<Theme> = {
  fontSize: (theme) => theme.typography.overline.fontSize,
  lineHeight: '1.125rem',
};

const secondaryTextSx: SxProps<Theme> = {
  ...overlineTextSx,
  color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
};

const titleTextSx: SxProps<Theme> = {
  ...overlineTextSx,
  fontWeight: 500,
  color: (theme) => theme.palette.custom.OV.sys.onSurface,
};

const fieldValueTextSx: SxProps<Theme> = {
  ...overlineTextSx,
  color: (theme) => theme.palette.custom.OV.sys.onSurface,
};

function getUrgencyColor(tone: SignalIncidentUrgencyTone, theme: Theme): string {
  const urgencyColorMap: Record<SignalIncidentUrgencyTone, string> = {
    'life-safety': theme.palette.error.main,
    urgent: theme.palette.warning.main,
    normal: theme.palette.info.main,
    low: theme.palette.success.main,
    neutral: theme.palette.custom.OV.sys.onSurfaceVariant,
  };

  return urgencyColorMap[tone];
}

function getAccentColor(isResolved: boolean, theme: Theme): string {
  return isResolved ? theme.palette.custom.OV.sys.success : theme.palette.error.main;
}

function getIconContainerSx(accentColor: string): SxProps<Theme> {
  return {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    mt: 0.25,
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50%',
    border: `1px solid ${alpha(accentColor, 0.16)}`,
    backgroundColor: alpha(accentColor, 0.12),
    color: accentColor,
  };
}

function getCardSx(accentColor: string, theme: Theme): SxProps<Theme> {
  const baseSurface =
    theme.palette.mode === 'dark'
      ? theme.palette.custom.OV.sys.surfaceContainerHigh
      : theme.palette.custom.OV.sys.surfaceContainerLowest;

  return {
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(accentColor, 0.12) : alpha(accentColor, 0.08),
    backgroundImage: `linear-gradient(90deg, ${baseSurface} 0%, ${baseSurface} 100%)`,
    backgroundBlendMode: 'overlay',
    borderLeft: `0.1875rem solid ${accentColor}`,
  };
}

function getUrgencyChipSx(urgencyColor: string, theme: Theme): SxProps<Theme> {
  return {
    height: '1.375rem',
    backgroundColor: alpha(urgencyColor, 0.12),
    color: urgencyColor,
    border: `1px solid ${alpha(urgencyColor, 0.2)}`,
    '& .MuiChip-label': {
      px: 1,
      fontSize: theme.typography.overline.fontSize,
      fontWeight: 500,
      lineHeight: '1.125rem',
    },
  };
}

const FieldRow = ({
  label,
  value,
  valueColor,
  valueFontWeight = 400,
}: {
  label: string;
  value: string;
  valueColor?: string;
  valueFontWeight?: number;
}): React.JSX.Element => {
  return (
    <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
      <Typography sx={secondaryTextSx}>{label}</Typography>
      <Typography sx={fieldValueTextSx} color={valueColor} fontWeight={valueFontWeight}>
        {value}
      </Typography>
    </Stack>
  );
};

interface SignalIncidentRowProps {
  incident: SignalIncidentRowIncident;
  canUpdateIncident?: boolean;
  isUpdating?: boolean;
  onUpdateIncident?: () => Promise<void> | void;
}

export const SignalIncidentRowView = ({
  incident,
  canUpdateIncident = false,
  isUpdating = false,
  onUpdateIncident,
}: SignalIncidentRowProps): React.JSX.Element => {
  const theme = useTheme();
  const { displayTime } = useDisplayTime();
  const isResolved = incident.displayState === 'resolved';
  let statusLabel = 'Unresolved';
  if (isResolved) {
    statusLabel = 'Resolved';
  } else if (isUpdating) {
    statusLabel = 'Resolving...';
  }
  const accentColor = getAccentColor(isResolved, theme);
  const accentTextColor = isResolved ? accentColor : theme.palette.custom.OV.sys.onSurface;
  const urgencyColor = incident.urgency ? getUrgencyColor(incident.urgency.tone, theme) : null;
  const iconContainerSx = getIconContainerSx(accentColor);
  const cardSx = getCardSx(accentColor, theme);
  const urgencyChipSx = urgencyColor ? getUrgencyChipSx(urgencyColor, theme) : null;
  const resolvedAtLabel = incident.resolvedAt
    ? `${getRelativeDayLabel(incident.resolvedAt)?.label ?? incident.resolvedAt} at ${displayTime(incident.resolvedAt)}`
    : null;

  return (
    <Stack direction="row" gap={1.5} sx={outerRowSx}>
      <Stack sx={iconContainerSx}>
        <DynamicIcon name={isResolved ? 'check_circle' : 'error'} sx={iconSx} />
      </Stack>

      <Stack
        data-testid={`signal-incident-row-${incident.signalIncidentId}`}
        flex="0 1 75%"
        maxWidth="75%"
        minWidth={0}
        px={2}
        py={1.75}
        borderRadius={1}
        border={`1px solid ${alpha(accentColor, 0.28)}`}
        sx={cardSx}
      >
        <Stack direction="row" justifyContent="space-between" gap={2} alignItems="flex-start">
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" minWidth={0}>
            <Typography sx={titleTextSx}>Signal Incident</Typography>
            {incident.urgency && urgencyChipSx ? (
              <Chip label={incident.urgency.label} size="small" sx={urgencyChipSx} />
            ) : null}
          </Stack>
          <Typography sx={secondaryTextSx} flexShrink={0}>
            <PartnerTimeZoneTooltip date={incident.createdAt} partnerId={incident.partnerId}>
              {displayTime(incident.createdAt)}
            </PartnerTimeZoneTooltip>
          </Typography>
        </Stack>

        <Stack gap={0.5} mt={1}>
          <FieldRow
            label="Status:"
            value={statusLabel}
            valueColor={accentTextColor}
            valueFontWeight={isResolved ? 500 : 400}
          />
          {isResolved && incident.resolverDisplayName ? (
            <FieldRow label="Resolved by:" value={incident.resolverDisplayName} />
          ) : null}
          {isResolved && resolvedAtLabel ? (
            <FieldRow label="Resolved at:" value={resolvedAtLabel} />
          ) : null}
        </Stack>

        <SignalIncidentNotificationLog
          log={incident.notificationLog}
          isResolved={isResolved}
        />

        {!isResolved && canUpdateIncident ? (
          <Stack mt={1.5} alignItems="flex-start">
            <Button
              size="small"
              variant="contained"
              disabled={isUpdating}
              sx={{ textTransform: 'none' }}
              onClick={() => onUpdateIncident?.()}
            >
              {isUpdating ? 'Resolving...' : 'Resolve incident'}
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};
